import client from './client';
import {
  Order,
  OrderSheet,
  OrderStatus,
  Product,
  SellerOrderSortBy,
  SellerCustomerSummary,
  SellerProductOrderMetricsBySheet,
  Store,
  Tag,
} from '../types';
import { DashboardSummary } from './dashboard';

export interface SellerDashboardPageData {
  stores: Store[];
  summary: DashboardSummary;
}

export interface StoreMetrics {
  orderCount: number;
  totalRevenue: number;
}

export interface SellerStoresPageData {
  stores: Store[];
  deletedStores: Store[];
}

export interface SellerStoreMetricsPageData {
  metricsByStore: Record<string, StoreMetrics>;
}

export interface SellerProductMetaPageData {
  stores: Store[];
  orderSheets: OrderSheet[];
  tags: Tag[];
}

export interface SellerProductsPageData {
  products: Product[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  hasMore: boolean;
  nextCursor: string | null;
}

export interface SellerProductMetricsPageData {
  activeProductQuantities: Record<string, number>;
  activeOptionQuantities: Record<string, number>;
  orderMetricsBySheet: SellerProductOrderMetricsBySheet;
}

export interface SellerOrderMetaPageData {
  stores: Store[];
  orderSheets: OrderSheet[];
  customerAliases: Record<string, string>;
}

export interface SellerOrderProductsPageData {
  products: Product[];
}

export interface SellerOrdersPageData {
  orders: Order[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  hasMore: boolean;
  nextCursor: string | null;
  statusCounts: {
    ALL: number;
    PENDING: number;
    READY: number;
    COMPLETED: number;
    CANCELLED: number;
  };
  summary: {
    totalOrders: number;
    totalAmount: number;
    totalItems: number;
    totalSkus: number;
  };
}

export interface SellerCustomersPageData {
  customerSummaries: SellerCustomerSummary[];
  stores: Store[];
  orderSheets: OrderSheet[];
  customerAliases: Record<string, string>;
}

export const getSellerDashboardPage = async (
  storeId?: string,
): Promise<SellerDashboardPageData> => {
  const response = await client.get('seller-pages/dashboard', {
    params: { storeId },
  });
  return response.data;
};

export const getSellerStoresPage = async (): Promise<SellerStoresPageData> => {
  const response = await client.get('seller-pages/stores');
  return response.data;
};

export const getSellerStoreMetricsPage =
  async (): Promise<SellerStoreMetricsPageData> => {
    const response = await client.get('seller-pages/store-metrics');
    return response.data;
  };

export type SellerProductSortBy =
  | 'DEFAULT'
  | 'STOCK_ASC'
  | 'STOCK_DESC'
  | 'ORDER_VOLUME_ASC'
  | 'ORDER_VOLUME_DESC'
  | 'AVAILABLE_STOCK_ASC'
  | 'AVAILABLE_STOCK_DESC'
  | 'WAITLIST_ASC'
  | 'WAITLIST_DESC';

export const getSellerProductMetaPage =
  async (): Promise<SellerProductMetaPageData> => {
    const response = await client.get('seller-pages/product-meta');
    return response.data;
  };

export const getSellerProductsPage = async (params?: {
  orderSheetIds?: string[];
  sortOrderSheetIds?: string[];
  search?: string;
  tag?: string | null;
  sortBy?: SellerProductSortBy;
  page?: number;
  take?: number;
}): Promise<SellerProductsPageData> => {
  const response = await client.get('seller-pages/products', {
    params: {
      orderSheetIds: params?.orderSheetIds?.join(','),
      sortOrderSheetIds: params?.sortOrderSheetIds?.join(','),
      search: params?.search?.trim() || undefined,
      tag: params?.tag?.trim() || undefined,
      sortBy: params?.sortBy,
      page: params?.page,
      take: params?.take,
    },
  });
  return response.data;
};

export const getSellerProductMetricsPage =
  async (params?: {
    productIds?: string[];
    orderSheetIds?: string[];
  }): Promise<SellerProductMetricsPageData> => {
    const response = await client.get('seller-pages/product-metrics', {
      params: {
        productIds: params?.productIds?.join(','),
        orderSheetIds: params?.orderSheetIds?.join(','),
      },
    });
    return response.data;
  };

export const getSellerOrderMetaPage =
  async (): Promise<SellerOrderMetaPageData> => {
    const response = await client.get('seller-pages/order-meta');
    return response.data;
  };

export const getSellerOrderProductsPage =
  async (): Promise<SellerOrderProductsPageData> => {
    const response = await client.get('seller-pages/order-products');
    return response.data;
  };

export const getSellerOrdersPage = async (params?: {
  orderSheetIds?: string[];
  search?: string;
  status?: 'ALL' | OrderStatus;
  page?: number;
  cursor?: string;
  take?: number;
  storeId?: string;
  sortBy?: SellerOrderSortBy;
}): Promise<SellerOrdersPageData> => {
  const response = await client.get('seller-pages/orders', {
    params: {
      orderSheetIds: params?.orderSheetIds?.join(','),
      search: params?.search?.trim() || undefined,
      status: params?.status && params.status !== 'ALL' ? params.status : undefined,
      page: params?.page,
      cursor: params?.cursor,
      take: params?.take,
      storeId: params?.storeId,
      sortBy: params?.sortBy,
    },
  });
  return response.data;
};

export const getSellerCustomersPage = async (): Promise<SellerCustomersPageData> => {
  const response = await client.get('seller-pages/customers');
  return response.data;
};
